import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Plus, Edit, Trash2, DollarSign, Clock, Tag,
  CheckCircle, XCircle, RefreshCw, Percent
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { packagesAPI, servicesAPI } from '../../services/api';
import { toast } from 'sonner';

const PackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    services: [],
    discount_percent: 10,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, servicesRes] = await Promise.all([
        packagesAPI.getAll({ include_expired: true }),
        servicesAPI.getAll()
      ]);
      setPackages(packagesRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      services: [],
      discount_percent: 10,
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      services: pkg.services.map(s => ({ service_id: s.service_id, quantity: s.quantity || 1 })),
      discount_percent: pkg.discount_percent,
      is_active: pkg.is_active
    });
    setShowModal(true);
  };

  const toggleService = (serviceId) => {
    setFormData(prev => {
      const exists = prev.services.find(s => s.service_id === serviceId);
      if (exists) {
        return { ...prev, services: prev.services.filter(s => s.service_id !== serviceId) };
      } else {
        return { ...prev, services: [...prev.services, { service_id: serviceId, quantity: 1 }] };
      }
    });
  };

  const calculatePrice = () => {
    let total = 0;
    formData.services.forEach(item => {
      const service = services.find(s => s.id === item.service_id);
      if (service) {
        total += (service.hourly_rate * (service.duration_hours || 1) * item.quantity);
      }
    });
    const discount = total * (formData.discount_percent / 100);
    return { base: total, discount, final: total - discount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services.length < 2) {
      toast.error('Please select at least 2 services for a package');
      return;
    }

    try {
      if (editingPackage) {
        await packagesAPI.update(editingPackage.id, formData);
        toast.success('Package updated');
      } else {
        await packagesAPI.create(formData);
        toast.success('Package created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await packagesAPI.delete(id);
      toast.success('Package deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const prices = calculatePrice();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="packages-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Service Packages</h1>
          <p className="text-[var(--text-muted)]">Bundle services together at a discount</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" /> Create Package
        </Button>
      </div>

      {/* Packages Grid */}
      {packages.length === 0 ? (
        <div className="card p-12 text-center">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium mb-2">No Packages Yet</h3>
          <p className="text-[var(--text-muted)] mb-4">
            Create service bundles to offer customers discounts on combined services
          </p>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" /> Create Your First Package
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-6"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg">{pkg.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    pkg.is_active ? 'bg-[var(--success)]/15 text-green-700' : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                  }`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(pkg)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id)} className="text-[var(--error)]">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-sm text-[var(--text-muted)] mb-4 line-clamp-2">{pkg.description}</p>
              
              {/* Services List */}
              <div className="space-y-1 mb-4">
                {pkg.services.slice(0, 3).map((s) => (
                  <div key={s.service_id || s.service_name} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-3 h-3 text-[var(--success)]" />
                    <span className="truncate">{s.service_name}</span>
                    {s.quantity > 1 && <span className="text-[var(--text-muted)]">x{s.quantity}</span>}
                  </div>
                ))}
                {pkg.services.length > 3 && (
                  <p className="text-xs text-[var(--text-muted)]">+{pkg.services.length - 3} more services</p>
                )}
              </div>
              
              {/* Pricing */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[var(--text-muted)] line-through">${pkg.total_base_price}</span>
                  <span className="text-sm bg-[var(--success)]/15 text-green-700 px-2 py-0.5 rounded-full">
                    Save {pkg.discount_percent}%
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[var(--primary)]">${pkg.final_price}</span>
                  <span className="text-sm text-[var(--text-muted)]">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {pkg.total_duration_hours}h total
                  </span>
                </div>
                <p className="text-xs text-[var(--success)] mt-1">You save ${pkg.savings}</p>
              </div>
              
              {/* Usage Stats */}
              <div className="mt-4 pt-4 border-t flex justify-between text-xs text-[var(--text-muted)]">
                <span>Used {pkg.times_used || 0} times</span>
                {pkg.max_uses && <span>Max: {pkg.max_uses}</span>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold">
                  {editingPackage ? 'Edit Package' : 'Create New Package'}
                </h2>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid gap-4">
                  <div>
                    <Label>Package Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Deep Clean + Laundry Bundle"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what's included in this package..."
                      rows={3}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Discount Percentage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={formData.discount_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                        className="w-24"
                      />
                      <Percent className="w-4 h-4 text-[var(--text-muted)]" />
                      <span className="text-sm text-[var(--text-muted)]">(max 50%)</span>
                    </div>
                  </div>
                </div>
                
                {/* Services Selection */}
                <div>
                  <Label className="mb-3 block">Select Services (minimum 2)</Label>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {services.map(service => {
                      const isSelected = formData.services.some(s => s.service_id === service.id);
                      return (
                        <div
                          key={service.id}
                          onClick={() => toggleService(service.id)}
                          className={`flex items-center justify-between p-3 border-b last:border-0 cursor-pointer transition-colors ${
                            isSelected ? 'bg-[#D4AF37]/10' : 'hover:bg-[var(--background-subtle)]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? 'bg-[var(--primary)] border-[var(--primary)]' : 'border-[var(--border-primary)]'
                            }`}>
                              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                              <p className="font-medium">{service.name}</p>
                              <p className="text-xs text-[var(--text-muted)]">
                                ${service.hourly_rate}/hr • {service.duration_hours}h
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-medium">
                            ${(service.hourly_rate * (service.duration_hours || 1)).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-2">
                    {formData.services.length} services selected
                  </p>
                </div>
                
                {/* Price Preview */}
                {formData.services.length >= 2 && (
                  <div className="bg-[var(--background-subtle)] rounded-lg p-4">
                    <h4 className="font-medium mb-3">Price Preview</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Base Price:</span>
                        <span>${prices.base.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[var(--success)]">
                        <span>Discount ({formData.discount_percent}%):</span>
                        <span>-${prices.discount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Final Price:</span>
                        <span className="text-[var(--primary)]">${prices.final.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`w-10 h-6 rounded-full transition-all ${
                      formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-[var(--background-paper)] shadow transition-all ${
                      formData.is_active ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <Label>Package is Active</Label>
                </div>
              </div>
              
              <div className="p-6 border-t flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default PackagesTab;
